import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Mechanic } from '../../../types';
import { format } from 'date-fns';
import { formatTimeForDisplay, NewBookingForm } from './helpers';

interface NewBookingDialogProps {
  newBookingDialogOpen: boolean;
  setNewBookingDialogOpen: (open: boolean) => void;
  shopName?: string;
  selectedDate: Date;
  availableTimeSlots: string[];
  slotBookingCounts: Record<string, number>;
  maxBookings: number;
  newBookingTimeSlot: string;
  handleNewBookingTimeSlotSelect: (timeSlot: string) => void;
  newBookingForm: NewBookingForm;
  handleNewBookingFormChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleNewBookingMechanicChange: (e: SelectChangeEvent) => void;
  mechanics: Mechanic[];
  selectedShopForTimeSlots: string;
  /** A full slot, or the server's answer when the booking failed. */
  error: string | null;
  saving: boolean;
  handleCreateNewBooking: () => void;
}

const TWO_COLUMNS = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  gap: 2,
} as const;

/** A labelled group of fields. */
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box component="section" aria-label={title}>
    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
      {title}
    </Typography>
    <Stack spacing={2}>{children}</Stack>
  </Box>
);

/** How full a slot is, for the time picker. */
function slotNote(booked: number, max: number): string {
  if (booked >= max) return 'Fully booked';
  if (booked > 0) return `${booked} of ${max} booked`;
  return 'Open';
}

/** Staff booking for the shop and day chosen in the time-slot view. */
const NewBookingDialog: React.FC<NewBookingDialogProps> = ({
  newBookingDialogOpen,
  setNewBookingDialogOpen,
  shopName,
  selectedDate,
  availableTimeSlots,
  slotBookingCounts,
  maxBookings,
  newBookingTimeSlot,
  handleNewBookingTimeSlotSelect,
  newBookingForm,
  handleNewBookingFormChange,
  handleNewBookingMechanicChange,
  mechanics,
  selectedShopForTimeSlots,
  error,
  saving,
  handleCreateNewBooking,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const close = () => setNewBookingDialogOpen(false);

  const complete =
    newBookingTimeSlot &&
    newBookingForm.customerName &&
    newBookingForm.customerEmail &&
    newBookingForm.customerPhone &&
    newBookingForm.vehicleModel &&
    newBookingForm.vehicleYear &&
    newBookingForm.serviceDescription;

  return (
    <Dialog
      open={newBookingDialogOpen}
      onClose={close}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby="new-booking-title"
      slotProps={{ paper: { sx: fullScreen ? { borderRadius: 0 } : undefined } }}
    >
      <DialogTitle id="new-booking-title" sx={{ pb: 0.5 }}>
        Schedule New Booking
      </DialogTitle>
      <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 2 }}>
        {shopName ? `${shopName} · ` : ''}
        {format(selectedDate, 'EEEE, MMMM d, yyyy')}
      </Typography>
      <DialogContent dividers>
        <Stack spacing={3}>
          {error && <Alert severity="error">{error}</Alert>}

          <Section title="Appointment">
            <Box sx={TWO_COLUMNS}>
              <FormControl fullWidth required>
                <InputLabel id="new-booking-time-label">Time</InputLabel>
                <Select
                  labelId="new-booking-time-label"
                  label="Time"
                  value={newBookingTimeSlot}
                  onChange={(e) => handleNewBookingTimeSlotSelect(e.target.value)}
                  renderValue={(slot) => formatTimeForDisplay(slot)}
                >
                  {availableTimeSlots.map((slot) => {
                    const booked = slotBookingCounts[slot] || 0;
                    return (
                      <MenuItem key={slot} value={slot} disabled={booked >= maxBookings}>
                        <ListItemText
                          primary={formatTimeForDisplay(slot)}
                          secondary={slotNote(booked, maxBookings)}
                        />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="new-booking-mechanic-label" shrink>
                  Mechanic
                </InputLabel>
                <Select
                  labelId="new-booking-mechanic-label"
                  label="Mechanic"
                  notched
                  displayEmpty
                  value={newBookingForm.mechanicId}
                  onChange={handleNewBookingMechanicChange}
                  renderValue={(id) =>
                    mechanics.find((mechanic) => mechanic.id === id)?.user?.name ?? (
                      <Box component="span" sx={{ color: 'text.secondary' }}>
                        Unassigned
                      </Box>
                    )
                  }
                >
                  <MenuItem value="">
                    <em>Unassigned</em>
                  </MenuItem>
                  {mechanics
                    .filter((mechanic) => mechanic.shopId === selectedShopForTimeSlots)
                    .map((mechanic) => (
                      <MenuItem key={mechanic.id} value={mechanic.id}>
                        <ListItemText
                          primary={mechanic.user?.name}
                          secondary={mechanic.specialization}
                        />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
          </Section>

          <Section title="Customer">
            <TextField
              name="customerName"
              label="Customer Name"
              value={newBookingForm.customerName}
              onChange={handleNewBookingFormChange}
              required
              fullWidth
              autoComplete="off"
            />
            <Box sx={TWO_COLUMNS}>
              <TextField
                name="customerEmail"
                label="Email"
                type="email"
                value={newBookingForm.customerEmail}
                onChange={handleNewBookingFormChange}
                required
                fullWidth
                autoComplete="off"
              />
              <TextField
                name="customerPhone"
                label="Phone"
                type="tel"
                value={newBookingForm.customerPhone}
                onChange={handleNewBookingFormChange}
                required
                fullWidth
                autoComplete="off"
              />
            </Box>
          </Section>

          <Section title="Vehicle">
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '6rem 1fr', sm: '7rem 1fr 1fr' },
                gap: 2,
              }}
            >
              <TextField
                name="vehicleYear"
                label="Year"
                value={newBookingForm.vehicleYear}
                onChange={handleNewBookingFormChange}
                required
                placeholder="2020"
                slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 4 } }}
              />
              <TextField
                name="vehicleModel"
                label="Make and model"
                value={newBookingForm.vehicleModel}
                onChange={handleNewBookingFormChange}
                required
                placeholder="Honda Civic"
              />
              <TextField
                name="licensePlate"
                label="License plate"
                value={newBookingForm.licensePlate}
                onChange={handleNewBookingFormChange}
                placeholder="8KDL 204"
                slotProps={{ htmlInput: { maxLength: 12, autoCapitalize: 'characters' } }}
                sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}
              />
            </Box>
          </Section>

          <Section title="Service">
            <TextField
              name="serviceDescription"
              label="Service needed"
              value={newBookingForm.serviceDescription}
              onChange={handleNewBookingFormChange}
              required
              fullWidth
              multiline
              minRows={3}
              placeholder="Oil change, brake inspection…"
            />
            <TextField
              name="comments"
              label="Notes"
              value={newBookingForm.comments}
              onChange={handleNewBookingFormChange}
              fullWidth
              multiline
              minRows={2}
              placeholder="Anything the mechanic should know"
            />
          </Section>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={close}>Cancel</Button>
        <Button
          onClick={handleCreateNewBooking}
          variant="contained"
          disabled={!complete || saving}
        >
          {saving ? 'Booking…' : 'Create Booking'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewBookingDialog;
